import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  formatResolvedPriceString,
  getProductNavigationUrl,
} from "@/components/care/utils";
import {
  trackCardEvent,
  trackCardViewed,
  type CardSource,
} from "@/components/chat/chatCardEvents";
import ChatProductCard from "@/components/chat/ChatProductCard";
import ChatViewCart, {
  CARD_BUTTON_CLASSES,
} from "@/components/chat/ChatViewCart";
import { useCartPanelMounted } from "@/components/ecomm/cartPanelPresence";
import { CartContext } from "@/contexts/cartContext";
import type {
  CommerceCardBlock,
  SavedOffer,
  TreatmentOffer,
} from "@/services/commerceOffers";
import {
  quoteAndAdd,
  type QuoteAndAddResult,
} from "@/services/commerceQuoteAndAdd";

type Outcome = QuoteAndAddResult["outcome"];

// Tech spec 5.4 copy. A server refusal's own text is never shown or logged.
const OUTCOME_MESSAGES: Record<Outcome, string> = {
  review: "The price or details changed. Check the card before adding.",
  view_cart: "This is already in your cart.",
  added: "Added to your cart.",
  not_added: "We couldn't confirm this was added to your cart.",
  unconfirmed: "We couldn't confirm your cart. Please check your cart.",
  retryable: "We couldn't check this product right now. Please try again.",
  unavailable: "Not available to add right now.",
};
const REFUSED_MESSAGE =
  "We couldn't add this right now. Please try again or view your cart.";
const RETRY_OUTCOMES: Outcome[] = ["retryable", "unconfirmed"];
const VIEW_CART_OUTCOMES: Outcome[] = [
  "added",
  "view_cart",
  "not_added",
  "unconfirmed",
];

const HTTPS_URL = /^https:\/\/\S+$/;
const PDP_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Shared with restored cards (ChatSavedCommerceCard) and program cards (ChatProgramCard).
export const offerImageUrl = ({
  image_url,
}: Pick<SavedOffer, "image_url">): string | undefined =>
  image_url && HTTPS_URL.test(image_url) ? image_url : undefined;
// The server sends pdp_slug only for an active new PDP.
export const offerPdpPath = ({ pdp_slug }: SavedOffer): string | undefined =>
  pdp_slug && PDP_SLUG.test(pdp_slug)
    ? getProductNavigationUrl(true, pdp_slug)
    : undefined;

type AddResult = { outcome: string };
type CardAdd<Offer, Result extends AddResult> = {
  offer: Offer;
  result: Promise<Result>;
  settled?: Result;
};
// The latest add per card. Closing the sidebar unmounts the card mid-add, so the
// guard lives here: a reopened card waits on the same add instead of starting one.
const cardAdds = new Map<string, CardAdd<unknown, AddResult>>();
export const cardAdd = <Offer, Result extends AddResult>(
  cardKey: string
): CardAdd<Offer, Result> | undefined =>
  cardAdds.get(cardKey) as CardAdd<Offer, Result> | undefined;
// Confirmed adds by offer_id. The history refresh drops offers already in the cart,
// so a restored card reads this to say "In your cart" rather than "No longer available."
const confirmedAdds = new Map<string, TreatmentOffer>();
export const confirmedAdd = (offerId: string): TreatmentOffer | undefined =>
  confirmedAdds.get(offerId);

/** Drops a session's settled tap results; an add still in flight keeps its double-tap guard. */
export const forgetSettledAdds = (sessionId: string): void => {
  for (const [cardKey, add] of cardAdds)
    if (add.settled && cardKey.startsWith(`${sessionId}:`))
      cardAdds.delete(cardKey);
};

// Try again is a new add: run re-quotes and re-reads the cart, never resubmits.
// Undefined while the card's add is in flight: one add per card, so one confirmed-add event.
export const startAdd = <Offer, Result extends AddResult>(
  cardKey: string,
  offer: Offer,
  run: (offer: Offer) => Promise<Result>,
  onAdded: (settled: Result) => void
): CardAdd<Offer, Result> | undefined => {
  const current = cardAdds.get(cardKey);
  if (current && !current.settled) return undefined;
  // Every run's result union includes unconfirmed.
  const result = run(offer).catch(() => ({ outcome: "unconfirmed" }) as Result);
  const add: CardAdd<Offer, Result> = { offer, result };
  // Once per add, however many mounted cards wait on it.
  result.then((settled) => {
    add.settled = settled;
    if (settled.outcome === "added") onAdded(settled);
  });
  cardAdds.set(cardKey, add);
  return add;
};

/** A tap result's spec line and the controls it offers. Shared with program cards. */
export const addStatus = (result?: { outcome: Outcome; refusal?: string }) => {
  const outcome = result?.outcome;
  // A refusal invites a retry; the retry re-quotes and re-reads the cart first.
  const refused = outcome !== "review" && result?.refusal !== undefined;
  const isRetry = !!outcome && (RETRY_OUTCOMES.includes(outcome) || refused);
  return {
    message: outcome && (refused ? REFUSED_MESSAGE : OUTCOME_MESSAGES[outcome]),
    isRetry,
    canTap: !outcome || outcome === "review" || isRetry,
    canViewCart: !!outcome && VIEW_CART_OUTCOMES.includes(outcome),
  };
};

export default function ChatCommerceCard({
  card,
  sessionId,
  messageIndex,
  source,
}: {
  card: CommerceCardBlock;
  sessionId: string;
  messageIndex: number;
  source: CardSource;
}) {
  const { setShowCart, refetchCart } = useContext(CartContext);
  const hasCartPanel = useCartPanelMounted();
  const { kind, offer_id, product_slug } = card.offer;
  const cardKey = `${sessionId}:${messageIndex}:${offer_id}`;
  // Seeded once: ChatMessage keys cards by offer_id, so another offer remounts.
  const [add, setAdd] = useState(() =>
    cardAdd<TreatmentOffer, QuoteAndAddResult>(cardKey)
  );
  const [result, setResult] = useState(add?.settled);

  useEffect(() => {
    add?.result.then(setResult);
  }, [add]);

  useEffect(
    () => trackCardViewed(cardKey, { kind, product_slug }, source),
    [cardKey, kind, product_slug, source]
  );

  const pending = !!add && !result;
  const offer =
    result?.outcome === "review" ? result.offer : (add?.offer ?? card.offer);

  const tap = (): void => {
    const next = startAdd(cardKey, offer, quoteAndAdd, () => {
      trackCardEvent("added", offer, source);
      confirmedAdds.set(offer.offer_id, offer);
      refetchCart();
      // Without a panel, opening it would only pop it open on the next page with one.
      if (hasCartPanel) setShowCart(true);
    });
    if (!next) return;
    trackCardEvent("addClicked", offer, source);
    setResult(undefined);
    setAdd(next);
  };

  const outcome = result?.outcome;
  const { message, isRetry, canTap, canViewCart } = addStatus(result);
  const pdpPath = outcome === "unavailable" ? undefined : offerPdpPath(offer);

  return (
    <ChatProductCard
      name={offer.name}
      description={offer.description}
      image_url={offerImageUrl(offer)}
      price={
        outcome === "unavailable"
          ? undefined
          : formatResolvedPriceString(offer.price)
      }
    >
      <div role="status" className="text-[var(--chat-text-xs)] leading-[1.5]">
        {message && <p>{message}</p>}
      </div>
      {(canTap || canViewCart || pdpPath) && (
        <div className="flex flex-wrap items-center gap-[var(--chat-space-03)]">
          {canTap && (
            <button
              type="button"
              onClick={tap}
              disabled={pending}
              className={CARD_BUTTON_CLASSES}
            >
              {pending ? "Adding…" : isRetry ? "Try again" : "Add to cart"}
            </button>
          )}
          {canViewCart && <ChatViewCart />}
          {pdpPath && (
            <Link
              to={pdpPath}
              aria-label={`View details for ${offer.name}`}
              className="text-[13px] font-semibold text-[var(--chat-black)] underline"
            >
              View details
            </Link>
          )}
        </div>
      )}
    </ChatProductCard>
  );
}
